import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Combobox as ComboboxPrimitive } from '@base-ui/react'
import { CirclePlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/components/new/lib'
import {
    Button,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxInput,
    ComboboxTrigger,
    useComboboxAnchor,
} from '@/components/new/ui'

import { ComboboxField } from './ComboboxField'
import { ValueSelectPresentationContext } from './presentationContext'
import {
    areFilterSelectorValuesEqual,
    getNormalizedComparableValue,
    isCloseByEscape,
    normalizeFilterSelectorValues,
    resolveFallbackSelectedOptions,
    toFilterSelectorValue,
} from './helpers'
import { useComboboxFieldSelection, useResolvedComboboxFieldSelectedOptions } from './selection-context'

import type {
    ComboboxFieldOption,
    ValueInputInputEventProps,
    ValueSelectChangeOption,
    ValueSelectInputProps,
} from './types'

type ValueInputSingleTriggerProps = ValueInputInputEventProps & {
    id?: string
    placeholder?: string
    className?: string
    disabled?: boolean
    showChevron: boolean
    showClear: boolean
    startAddon?: React.ReactNode
    endAddon?: React.ReactNode
    prefix?: React.ReactNode
    invalid?: boolean
}

function ValueInputSingleTrigger({
    id,
    autoFocus,
    onBlur,
    onFocus,
    onKeyDown,
    placeholder,
    className,
    disabled,
    showChevron,
    showClear,
    startAddon,
    endAddon,
    prefix,
    invalid,
}: ValueInputSingleTriggerProps) {
    const { t } = useTranslation()
    const { isHydratingValue } = useComboboxFieldSelection()

    return (
        <ComboboxInput
            id={id}
            autoFocus={!!autoFocus}
            aria-invalid={invalid || undefined}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            disabled={disabled || isHydratingValue}
            showTrigger={showChevron}
            showClear={showClear}
            startAddon={startAddon}
            endAddon={endAddon}
            prefix={prefix}
            placeholder={
                isHydratingValue
                    ? t('components.control.combobox.loading')
                    : (placeholder ?? t('components.control.combobox.placeholder'))
            }
            className={className}
        />
    )
}

type ValueInputMultipleTriggerProps = ValueInputInputEventProps & {
    id?: string
    placeholder?: string
    className?: string
    disabled?: boolean
    anchorRef: ReturnType<typeof useComboboxAnchor>
    isAsyncMode: boolean
    draftValues: string[]
    fallbackSelectedOptions: ComboboxFieldOption[]
    showChevron: boolean
    endAddon?: React.ReactNode
    prefix?: React.ReactNode
    invalid?: boolean
}

function ValueInputMultipleTrigger({
    id,
    autoFocus,
    onBlur,
    onFocus,
    onKeyDown,
    placeholder,
    className,
    disabled,
    anchorRef,
    isAsyncMode,
    draftValues,
    fallbackSelectedOptions,
    showChevron,
    endAddon,
    prefix,
    invalid,
}: ValueInputMultipleTriggerProps) {
    const { t } = useTranslation()
    const { selectedOptions, isHydratingValue } = useResolvedComboboxFieldSelectedOptions({
        isAsyncMode,
        selectedValueCount: draftValues.length,
        fallbackSelectedOptions,
    })

    return (
        <ComboboxChips
            ref={anchorRef}
            aria-invalid={invalid || undefined}
            className={cn(
                prefix && 'pl-1',
                endAddon && showChevron ? 'pr-16' : (endAddon || showChevron) && 'pr-9',
                className
            )}
        >
            {prefix && <span className="flex shrink-0 items-center">{prefix}</span>}
            {selectedOptions.map((option) => (
                <ComboboxChip key={option.value}>{option.label}</ComboboxChip>
            ))}
            <ComboboxChipsInput
                id={id}
                aria-invalid={invalid || undefined}
                placeholder={
                    selectedOptions.length === 0
                        ? isHydratingValue
                            ? t('components.control.combobox.loading')
                            : placeholder
                        : undefined
                }
                autoFocus={!!autoFocus}
                onBlur={onBlur}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
            />
            {(endAddon || showChevron) && (
                <span className="absolute inset-y-1.5 right-1.5 flex items-center justify-center gap-1">
                    {endAddon}
                    {showChevron && (
                        <ComboboxTrigger
                            disabled={disabled || isHydratingValue}
                            render={<Button variant="ghost" size="icon-xs" className="h-6 w-6" />}
                        />
                    )}
                </span>
            )}
        </ComboboxChips>
    )
}

type FreeSoloComboboxFieldOption = ComboboxFieldOption & {
    isCustomValue?: boolean
}

export const ValueInput = ({
    id,
    value,
    onChange,
    options,
    asyncOptions,
    buildAsyncOptions,
    multiple = false,
    placeholder,
    freeSolo = false,
    freeSoloLabel,
    normalizeCaseInsensitive = true,
    className,
    startAddon,
    endAddon,
    prefix,
    queryOnOpen = true,
    showChevron = true,
    showClear = false,
    autoFocus,
    onBlur,
    onFocus,
    onKeyDown,
    renderOption,
    ...comboboxProps
}: ValueSelectInputProps) => {
    const { t } = useTranslation()
    const { contentProps: contentPropsProp, listProps: listPropsProp, ...restComboboxProps } = comboboxProps
    const invalid = !!comboboxProps['aria-invalid']
    const normalizedIncomingValues = useMemo(
        () => normalizeFilterSelectorValues({ value, multiple, normalizeCaseInsensitive }),
        [multiple, normalizeCaseInsensitive, value]
    )
    const [draftValues, setDraftValues] = useState(normalizedIncomingValues)
    const [inputValue, setInputValue] = useState('')
    const presentation = useContext(ValueSelectPresentationContext)
    const [localOpen, setLocalOpen] = useState(false)
    const isOpen = presentation?.open ?? localOpen
    const setIsOpen = useCallback(
        (open: boolean, eventDetails?: ComboboxPrimitive.Root.ChangeEventDetails) => {
            presentation?.onOpenChange(open, eventDetails)
            if (!eventDetails?.isCanceled) {
                setLocalOpen(open)
            }
        },
        [presentation]
    )
    const draftValuesRef = useRef(draftValues)
    const inputValueRef = useRef(inputValue)
    const anchorRef = useComboboxAnchor()

    useEffect(() => {
        draftValuesRef.current = draftValues
    }, [draftValues])

    useEffect(() => {
        if (
            areFilterSelectorValuesEqual({
                left: draftValuesRef.current,
                right: normalizedIncomingValues,
                normalizeCaseInsensitive,
            })
        ) {
            return
        }

        draftValuesRef.current = normalizedIncomingValues
        setDraftValues(normalizedIncomingValues)
    }, [normalizeCaseInsensitive, normalizedIncomingValues])

    const resolvedAsyncOptions = useMemo(
        () => (buildAsyncOptions ? buildAsyncOptions(draftValues) : asyncOptions),
        [asyncOptions, buildAsyncOptions, draftValues]
    )

    const fallbackSelectedOptions = useMemo(
        () =>
            resolveFallbackSelectedOptions({
                values: draftValues,
                availableOptions: options,
                normalizeCaseInsensitive,
            }),
        [draftValues, normalizeCaseInsensitive, options]
    )

    const applyNextValues = useCallback(
        (nextValues: string[], nextOption: ValueSelectChangeOption) => {
            if (
                areFilterSelectorValuesEqual({
                    left: nextValues,
                    right: draftValuesRef.current,
                    normalizeCaseInsensitive,
                })
            ) {
                return
            }

            draftValuesRef.current = nextValues
            setDraftValues(nextValues)
            onChange(toFilterSelectorValue(nextValues, multiple), nextOption)
        },
        [multiple, normalizeCaseInsensitive, onChange]
    )

    const resolveMatchingOption = useCallback(
        (rawInputValue: string): ComboboxFieldOption | null => {
            const trimmedValue = rawInputValue.trim()

            if (!trimmedValue || multiple) {
                return null
            }

            const normalizedValue = getNormalizedComparableValue(trimmedValue, normalizeCaseInsensitive)
            const availableOptions = options || []

            const exactMatch =
                availableOptions.find((option) => {
                    const normalizedOptionValue = getNormalizedComparableValue(option.value, normalizeCaseInsensitive)
                    const normalizedOptionLabel = getNormalizedComparableValue(option.label, normalizeCaseInsensitive)

                    return normalizedOptionValue === normalizedValue || normalizedOptionLabel === normalizedValue
                }) || null

            if (exactMatch) {
                return exactMatch
            }

            const filter = restComboboxProps.filter

            if (!filter) {
                return null
            }

            const filteredOptions = availableOptions.filter((option) => filter(option, trimmedValue))

            return filteredOptions.length === 1 ? filteredOptions[0] : null
        },
        [multiple, normalizeCaseInsensitive, options, restComboboxProps.filter]
    )

    const commitPendingInputValue = useCallback(() => {
        if (multiple) {
            return
        }

        const matchingOption = resolveMatchingOption(inputValueRef.current)

        if (!matchingOption) {
            return
        }

        applyNextValues([matchingOption.value], matchingOption)
    }, [applyNextValues, multiple, resolveMatchingOption])

    const handleValueChange = useCallback(
        (nextValue: string | string[] | null, nextOption: ComboboxFieldOption | ComboboxFieldOption[] | null) => {
            const nextValues = normalizeFilterSelectorValues({
                value: nextValue,
                multiple,
                normalizeCaseInsensitive,
            })

            applyNextValues(nextValues, nextOption)
            inputValueRef.current = ''
            setInputValue('')
        },
        [applyNextValues, multiple, normalizeCaseInsensitive]
    )

    const handleOpenChange = useCallback(
        (nextOpen: boolean, eventDetails?: ComboboxPrimitive.Root.ChangeEventDetails) => {
            setIsOpen(nextOpen, eventDetails)

            if (eventDetails?.isCanceled) {
                return
            }

            if (!nextOpen) {
                if (!isCloseByEscape(eventDetails)) {
                    commitPendingInputValue()
                }

                inputValueRef.current = ''
                setInputValue('')
            }
        },
        [commitPendingInputValue, setIsOpen]
    )

    const handleBlur = useCallback<NonNullable<ValueInputInputEventProps['onBlur']>>(
        (event) => {
            commitPendingInputValue()
            onBlur?.(event)
        },
        [commitPendingInputValue, onBlur]
    )

    const handleInputValueChange = useCallback(
        (nextInputValue: string, eventDetails?: ComboboxPrimitive.Root.ChangeEventDetails) => {
            if (eventDetails?.reason !== 'input-change') {
                return
            }

            inputValueRef.current = nextInputValue
            setInputValue(nextInputValue)

            if (!isOpen && nextInputValue.trim()) {
                setIsOpen(true)
            }
        },
        [isOpen, setIsOpen]
    )

    const trimmedInputValue = inputValue.trim()
    const normalizedInputValue = trimmedInputValue
        ? getNormalizedComparableValue(trimmedInputValue, normalizeCaseInsensitive)
        : ''
    const canCreateCustomValue = useMemo(() => {
        if (!freeSolo || !trimmedInputValue) {
            return false
        }

        const hasMatchingSelectedValue = draftValues.some(
            (draftValue) => getNormalizedComparableValue(draftValue, normalizeCaseInsensitive) === normalizedInputValue
        )
        if (hasMatchingSelectedValue) {
            return false
        }

        return !(options || []).some((option) => {
            const normalizedOptionValue = getNormalizedComparableValue(option.value, normalizeCaseInsensitive)
            const normalizedOptionLabel = getNormalizedComparableValue(option.label, normalizeCaseInsensitive)
            return normalizedOptionValue === normalizedInputValue || normalizedOptionLabel === normalizedInputValue
        })
    }, [draftValues, freeSolo, normalizeCaseInsensitive, normalizedInputValue, options, trimmedInputValue])

    const freeSoloOptions = useMemo<FreeSoloComboboxFieldOption[]>(
        () =>
            canCreateCustomValue
                ? [
                      {
                          value: trimmedInputValue,
                          label: trimmedInputValue,
                          isCustomValue: true,
                      },
                  ]
                : [],
        [canCreateCustomValue, trimmedInputValue]
    )

    const resolvedRenderOption = useCallback(
        (option: ComboboxFieldOption) => {
            if ((option as FreeSoloComboboxFieldOption).isCustomValue) {
                return (
                    <span className="inline-flex min-w-0 items-center gap-2">
                        <CirclePlusIcon className="size-4 shrink-0" />
                        <span className="min-w-0 truncate">
                            {freeSoloLabel
                                ? freeSoloLabel(option.label)
                                : t('components.control.combobox.createOption', { query: option.label })}
                        </span>
                    </span>
                )
            }

            if (renderOption) {
                return renderOption(option)
            }

            return option.label
        },
        [freeSoloLabel, renderOption, t]
    )

    const isAsyncMode = !!resolvedAsyncOptions
    const resolvedContentProps = useMemo(
        () => ({
            ...contentPropsProp,
            anchor: multiple && !presentation ? (contentPropsProp?.anchor ?? anchorRef) : contentPropsProp?.anchor,
            className: cn('flex max-h-(--available-height) min-h-0 flex-col', contentPropsProp?.className),
        }),
        [anchorRef, contentPropsProp, multiple, presentation]
    )
    const resolvedListProps = useMemo(
        () => ({
            ...listPropsProp,
            className: cn('min-h-0 flex-1 max-h-none overflow-y-auto', listPropsProp?.className),
        }),
        [listPropsProp]
    )

    const triggerComponent = presentation ? (
        <ComboboxPrimitive.Trigger render={<Button {...presentation.buttonProps} />}>
            {presentation.children}
        </ComboboxPrimitive.Trigger>
    ) : multiple ? (
        <ValueInputMultipleTrigger
            id={id}
            autoFocus={autoFocus}
            onBlur={handleBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={className}
            disabled={restComboboxProps.disabled}
            anchorRef={anchorRef}
            isAsyncMode={isAsyncMode}
            draftValues={draftValues}
            fallbackSelectedOptions={fallbackSelectedOptions}
            showChevron={showChevron}
            endAddon={endAddon}
            prefix={prefix}
            invalid={invalid}
        />
    ) : (
        <ValueInputSingleTrigger
            id={id}
            autoFocus={autoFocus}
            onBlur={handleBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            className={className}
            disabled={restComboboxProps.disabled}
            showChevron={showChevron}
            showClear={showClear}
            startAddon={startAddon}
            endAddon={endAddon}
            prefix={prefix}
            invalid={invalid}
        />
    )

    return (
        <ComboboxField
            {...restComboboxProps}
            multiple={multiple}
            open={isOpen}
            value={toFilterSelectorValue(draftValues, multiple)}
            searchValue={inputValue}
            inputValue={multiple || presentation ? inputValue : undefined}
            options={options}
            extraOptions={freeSoloOptions}
            asyncOptions={resolvedAsyncOptions}
            onValueChange={handleValueChange}
            onOpenChange={handleOpenChange}
            onInputValueChange={handleInputValueChange}
            queryOnOpen={queryOnOpen}
            placeholder={placeholder}
            showSearchInput={!!presentation}
            contentProps={resolvedContentProps}
            listProps={resolvedListProps}
            triggerComponent={triggerComponent}
            renderOption={resolvedRenderOption}
        />
    )
}
